import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [openMenu, setOpenMenu] = useState('');
  const [searchActive, setSearchActive] = useState(false);
  const [searchText, setSearchText] = useState('');
  const inputRef = useRef(null);

  const searchFormRef = useRef(null);
  const searchBtnRef = useRef(null);

  const [forceHeaderWhite, setForceHeaderWhite] = useState(false);

  const [isScrolled, setIsScrolled] = useState(false);
  const isWebMenuOpen = openMenu === 'web';

  const isForcedScrolledHeader = location.pathname === '/sitemap' || location.pathname.startsWith('/integratedSearch');

  const handleMenuClose = () => {
    setOpenMenu('');
    setSearchActive(false);
    setForceHeaderWhite(false);
  };

  const handleMenuCloseOnly = () => {
    setOpenMenu('');
  };

  const handleMenu = (type) => {
    if (type === openMenu) setOpenMenu('');
    else setOpenMenu(type);

    if (type) {
      setSearchActive(false);
      setForceHeaderWhite(false);
    }
  };

  const openSearch = () => {
    setSearchActive(true);
    setForceHeaderWhite(true);
    setOpenMenu('');
  };

  const closeSearch = () => {
    setSearchActive(false);
    setSearchText('');
    setForceHeaderWhite(false);
  };

  const toggleSearch = () => {
    if (searchActive) closeSearch();
    else openSearch();
  };

  const movePage = (path) => {
    setOpenMenu('');
    setSearchActive(false);
    setForceHeaderWhite(false);
    navigate(path);
  };

  const moveBusiness = (path) => {
    setOpenMenu('');
    setSearchActive(false);
    setForceHeaderWhite(false);
    navigate(path);
  };

  const moveBoard = (cate) => {
    setOpenMenu('');
    setSearchActive(false);
    setForceHeaderWhite(false);
    navigate(`/board/${cate}`, { state: { cate } });
  };

  const handlerIntegratedSearch = () => {
    if (!searchText.trim()) return;
    navigate(`/integratedSearch?searchText=${searchText}`);
    setSearchActive(false);
    setSearchText('');
    setForceHeaderWhite(false);
    setOpenMenu('');
  };

  useEffect(() => {
    if (searchActive && inputRef.current) inputRef.current.focus();
  }, [searchActive]);

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handlerIntegratedSearch();
    }
  };

  useEffect(() => {
    if (!searchActive) return;
    const onKey = (e) => {
      if (e.key === 'Escape') closeSearch();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [searchActive]);

  useEffect(() => {
    if (!searchActive) return;

    const onDown = (e) => {
      const t = e.target;
      const inForm = searchFormRef.current && searchFormRef.current.contains(t);
      const inBtn = searchBtnRef.current && searchBtnRef.current.contains(t);
      if (inForm || inBtn) return;
      closeSearch();
    };

    document.addEventListener('mousedown', onDown, true);
    return () => document.removeEventListener('mousedown', onDown, true);
  }, [searchActive]);

  useEffect(() => {
    const handleScroll = (event) => {
      const target = event.target;
      const scrollElement = target === document ? document.scrollingElement || document.documentElement : target;
      const scrollTop = scrollElement.scrollTop || 0;
      setIsScrolled(scrollTop > 100);
    };

    document.addEventListener('scroll', handleScroll, { passive: true, capture: true });
    return () => document.removeEventListener('scroll', handleScroll, true);
  }, []);

  const headerActive = isScrolled || isWebMenuOpen || isForcedScrolledHeader || forceHeaderWhite;

  const headerStyle = {
    backgroundColor: headerActive ? '#ffffff' : 'transparent',
    boxShadow: headerActive ? '0 2px 6px rgba(0, 0, 0, 0.08)' : 'none',
    transition: 'background-color 0.3s ease, box-shadow 0.3s ease',
  };

  const isTop = !isScrolled && !isWebMenuOpen && !isForcedScrolledHeader && !forceHeaderWhite;

  const menuTextStyle = {
    color: isTop ? '#ffffff' : '#111827',
    transition: 'color 0.3s ease',
  };

  /**
   * ✅ 모바일 메뉴 구성: PC 헤더의 "메인 메뉴"와 1:1 동일하게 맞춤
   * PC 메인 메뉴: 알림 및 소식 / 사업분야 / 기업연구동 / 보유시설 / 융합원소개
   */
  const mobileMenus = [
    {
      key: 'notice',
      left: '알림 및 소식',
      rightTitle: '알림 및 소식',
      items: [
        { label: '공지사항', onClick: () => moveBoard('notice') },
        { label: '사업공고', onClick: () => moveBoard('business') },
        { label: '교육공고', onClick: () => moveBoard('education') },
        { label: '채용공고', onClick: () => moveBoard('employment') },
        { label: '입찰공고', onClick: () => moveBoard('bid') },
        { label: '보도자료', onClick: () => moveBoard('news') },
        { label: '자료실', onClick: () => moveBoard('reference') },
        { label: '융합원 아카이브', onClick: () => moveBoard('archive') },
      ],
    },
    {
      key: 'business',
      left: '사업분야',
      rightTitle: '사업분야',
      items: [
        { label: '산학융합지구조성사업', onClick: () => moveBusiness('/fusionSupportProgram') },
        { label: '기업지원사업', onClick: () => moveBusiness('/business/chungnamInnovationProject') },
        { label: '인재양성사업', onClick: () => moveBusiness('/business/localIndustryTalentDevelopment') },
        { label: '창업육성사업', onClick: () => moveBusiness('/business/dangjinStartupSupport') },
      ],
    },
    {
      key: 'company',
      left: '기업연구동',
      rightTitle: '기업연구동',
      items: [
        { label: '입주안내', onClick: () => movePage('/inquiryinfo') },
        { label: '층별안내', onClick: () => movePage('/floor') },
        { label: '입주기업 현황', onClick: () => movePage('/company') },
        { label: '입주문의', onClick: () => moveBoard('inquiry') },
      ],
    },
    {
      key: 'facility',
      left: '보유시설',
      rightTitle: '보유시설',
      items: [
        { label: '회의실', onClick: () => movePage('/meetingroom') },
        { label: '전산실습실', onClick: () => movePage('/computer') },
        { label: '3D프린터', onClick: () => movePage('/printing') },
        { label: 'AR/VR', onClick: () => movePage('/arvr') },
        { label: 'CNC/MCT', onClick: () => movePage('/cnc') },
        {
          label: '시설예약',
          onClick: () => {
            navigate('/board/facility/write', { state: { step: 1 } });
            handleMenuClose();
          },
        },
      ],
    },
    {
      key: 'intro',
      left: '융합원소개',
      rightTitle: '융합원소개',
      items: [
        { label: '인사말', onClick: () => movePage('/intro') },
        { label: '비전 및 목표', onClick: () => movePage('/vision') },
        { label: '연혁', onClick: () => movePage('/history') },
        { label: '조직도', onClick: () => movePage('/organization') },
        { label: '소개자료', onClick: () => movePage('/introduce') },
        { label: '관리규정', onClick: () => movePage('/regulations') },
        { label: '오시는 길', onClick: () => movePage('/map') },
      ],
    },
  ];

  const [mobileTab, setMobileTab] = useState(mobileMenus[0].key);
  const currentMobile = mobileMenus.find((m) => m.key === mobileTab) || mobileMenus[0];

  useEffect(() => {
    if (openMenu === 'mobile') setMobileTab(mobileMenus[0].key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openMenu]);

  return (
    <div className="header_area">
      <div
        className={`header_wrap${isScrolled ? ' scrolled' : ''}${openMenu === 'web' ? ' menu_active' : ''}`}
        style={headerStyle}
        // ✅ hover 유지 문제 때문에 header_wrap onMouseLeave로 닫는거 금지
        // onMouseLeave={handleMenuCloseOnly}
      >
        <div className="header_bottom_box">
          <div className="header_back">
            <div
              className="logo_box"
              onClick={() => {
                setOpenMenu('');
                setSearchActive(false);
                setForceHeaderWhite(false);
                navigate('/');
              }}
            >
              <div className={`logo_img ${isTop ? 'white' : 'default'}`}></div>
            </div>

            <div className="right_box">
              <div
                className="menu_box"
                // ✅ PC 드롭다운은 "각 main_menu 내부 menu_row"만 사용 (전체폭 패널 제거)
                //    menu_box를 벗어나면 닫아주면 hover 유지/정리 깔끔
                onMouseLeave={() => {
                  if (openMenu === 'web') setOpenMenu('');
                }}
              >
                <div
                  className="main_menu"
                  style={menuTextStyle}
                  onMouseEnter={() => {
                    setSearchActive(false);
                    setForceHeaderWhite(false);
                    setOpenMenu('web');
                  }}
                >
                  알림 및 소식
                  <div className={`menu_row ${openMenu === 'web' && 'active'}`}>
                    <div className="sub_menu_box">
                      <div className="sub_menu" onClick={() => moveBoard('notice')}>
                        공지사항
                      </div>
                      <div className="sub_menu" onClick={() => moveBoard('business')}>
                        사업공고
                      </div>
                      <div className="sub_menu" onClick={() => moveBoard('education')}>
                        교육공고
                      </div>
                      <div className="sub_menu" onClick={() => moveBoard('employment')}>
                        채용공고
                      </div>
                      <div className="sub_menu" onClick={() => moveBoard('bid')}>
                        입찰공고
                      </div>
                      <div className="sub_menu" onClick={() => moveBoard('news')}>
                        보도자료
                      </div>
                      <div className="sub_menu" onClick={() => moveBoard('reference')}>
                        자료실
                      </div>
                      <div className="sub_menu" onClick={() => moveBoard('archive')}>
                        융합원 아카이브
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  className="main_menu"
                  style={menuTextStyle}
                  onMouseEnter={() => {
                    setSearchActive(false);
                    setForceHeaderWhite(false);
                    setOpenMenu('web');
                  }}
                >
                  사업분야
                  <div className={`menu_row ${openMenu === 'web' && 'active'}`}>
                    <div className="sub_menu_box">
                      <div className="sub_menu" onClick={() => moveBusiness('/fusionSupportProgram')}>
                        산학융합지구조성사업
                      </div>
                      <div className="sub_menu" onClick={() => moveBusiness('/business/chungnamInnovationProject')}>
                        기업지원사업
                      </div>
                      <div
                        className="sub_menu"
                        onClick={() => moveBusiness('/business/localIndustryTalentDevelopment')}
                      >
                        인재양성사업
                      </div>
                      <div className="sub_menu" onClick={() => moveBusiness('/business/dangjinStartupSupport')}>
                        창업육성사업
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  className="main_menu"
                  style={menuTextStyle}
                  onMouseEnter={() => {
                    setSearchActive(false);
                    setForceHeaderWhite(false);
                    setOpenMenu('web');
                  }}
                >
                  기업연구동
                  <div className={`menu_row ${openMenu === 'web' && 'active'}`}>
                    <div className="sub_menu_box">
                      <div className="sub_menu" onClick={() => movePage('/inquiryinfo')}>
                        입주안내
                      </div>
                      <div className="sub_menu" onClick={() => movePage('/floor')}>
                        층별안내
                      </div>
                      <div className="sub_menu" onClick={() => movePage('/company')}>
                        입주기업 현황
                      </div>
                      <div className="sub_menu" onClick={() => moveBoard('inquiry')}>
                        입주문의
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  className="main_menu"
                  style={menuTextStyle}
                  onMouseEnter={() => {
                    setSearchActive(false);
                    setForceHeaderWhite(false);
                    setOpenMenu('web');
                  }}
                >
                  보유시설
                  <div className={`menu_row ${openMenu === 'web' && 'active'}`}>
                    <div className="sub_menu_box">
                      <div className="sub_menu" onClick={() => movePage('/meetingroom')}>
                        회의실
                      </div>
                      <div className="sub_menu" onClick={() => movePage('/computer')}>
                        전산실습실
                      </div>
                      <div className="sub_menu" onClick={() => movePage('/printing')}>
                        3D프린터
                      </div>
                      <div className="sub_menu" onClick={() => movePage('/arvr')}>
                        AR/VR
                      </div>
                      <div className="sub_menu" onClick={() => movePage('/cnc')}>
                        CNC/MCT
                      </div>
                      <div
                        className="sub_menu"
                        onClick={() => {
                          navigate('/board/facility/write', { state: { step: 1 } });
                          handleMenuClose();
                        }}
                      >
                        시설예약
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  className="main_menu"
                  style={menuTextStyle}
                  onMouseEnter={() => {
                    setSearchActive(false);
                    setForceHeaderWhite(false);
                    setOpenMenu('web');
                  }}
                >
                  융합원소개
                  <div className={`menu_row ${openMenu === 'web' && 'active'}`}>
                    <div className="sub_menu_box">
                      <div className="sub_menu" onClick={() => movePage('/intro')}>
                        인사말
                      </div>
                      <div className="sub_menu" onClick={() => movePage('/vision')}>
                        비전 및 목표
                      </div>
                      <div className="sub_menu" onClick={() => movePage('/history')}>
                        연혁
                      </div>
                      <div className="sub_menu" onClick={() => movePage('/organization')}>
                        조직도
                      </div>
                      <div className="sub_menu" onClick={() => movePage('/introduce')}>
                        소개자료
                      </div>
                      <div className="sub_menu" onClick={() => movePage('/regulations')}>
                        관리규정
                      </div>
                      <div className="sub_menu" onClick={() => movePage('/map')}>
                        오시는 길
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="icon_box">
                <button
                  type="button"
                  className="icon_btn menu_btn web"
                  onClick={() => {
                    setSearchActive(false);
                    setForceHeaderWhite(false);
                    setOpenMenu((prev) => (prev === 'web' ? '' : 'web'));
                  }}
                  aria-label="전체 메뉴 열기"
                >
                  <svg viewBox="0 0 24 24" className="icon_svg" fill="none" stroke="currentColor">
                    <line x1="4" y1="7" x2="20" y2="7" />
                    <line x1="4" y1="12" x2="20" y2="12" />
                    <line x1="4" y1="17" x2="20" y2="17" />
                  </svg>
                </button>

                <button
                  type="button"
                  className="icon_btn menu_btn mobile"
                  onClick={() => {
                    setSearchActive(false);
                    setForceHeaderWhite(false);
                    setOpenMenu((prev) => (prev === 'mobile' ? '' : 'mobile'));
                  }}
                  aria-label="모바일 메뉴 열기"
                >
                  <svg viewBox="0 0 24 24" className="icon_svg" fill="none" stroke="currentColor">
                    <line x1="4" y1="6" x2="20" y2="6" />
                    <line x1="4" y1="12" x2="20" y2="12" />
                    <line x1="4" y1="18" x2="20" y2="18" />
                  </svg>
                </button>

                <div className="search_icon_box">
                  <button
                    ref={searchBtnRef}
                    type="button"
                    className={`icon_btn search_btn ${searchActive ? 'active' : ''}`}
                    onClick={toggleSearch}
                    aria-label="통합검색"
                  >
                    {searchActive ? (
                      <svg viewBox="0 0 24 24" className="icon_svg" fill="none" stroke="currentColor">
                        <line x1="5" y1="5" x2="19" y2="19" />
                        <line x1="5" y1="19" x2="19" y2="5" />
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" className="icon_svg" fill="none" stroke="currentColor">
                        <circle cx="11" cy="11" r="5.5" />
                        <line x1="15.5" y1="15.5" x2="19" y2="19" />
                      </svg>
                    )}
                  </button>

                  {searchActive && (
                    <div className="search_form" ref={searchFormRef}>
                      <div className="search_title">통합검색</div>

                      <div className="search_row">
                        <input
                          id="search_form"
                          ref={inputRef}
                          type="text"
                          placeholder="검색어를 입력하세요."
                          value={searchText}
                          onChange={(e) => setSearchText(e.target.value)}
                          onKeyDown={handleKeyDown}
                        />

                        <button
                          type="button"
                          className="search_submit"
                          onClick={handlerIntegratedSearch}
                          aria-label="검색 실행"
                        >
                          <svg viewBox="0 0 24 24" className="icon_svg inner" fill="none" stroke="currentColor">
                            <circle cx="11" cy="11" r="5.5" />
                            <line x1="15.5" y1="15.5" x2="19" y2="19" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <button
                  type="button"
                  className="icon_btn sitemap_btn"
                  onClick={() => {
                    setOpenMenu('');
                    setSearchActive(false);
                    setForceHeaderWhite(false);
                    navigate('/sitemap');
                  }}
                  aria-label="사이트맵"
                >
                  <svg viewBox="0 0 24 24" className="icon_svg" fill="none" stroke="currentColor">
                    <rect x="4" y="4" width="6" height="6" rx="1.5" />
                    <rect x="14" y="4" width="6" height="6" rx="1.5" />
                    <rect x="4" y="14" width="6" height="6" rx="1.5" />
                    <rect x="14" y="14" width="6" height="6" rx="1.5" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ✅ 모바일 메뉴 (원본 유지) */}
        <div className={`menu_show_area ${openMenu === 'mobile' ? 'active' : ''}`}>
          <div className="menu_show_box mobile">
            <div className="show_menu_back mobile">
              <div className="mnav_wrap">
                <div className="mnav_top">
                  <div className="mnav_logo" onClick={() => movePage('/')}>
                    <div className="mnav_logo_img"></div>
                  </div>

                  <button type="button" className="mnav_close" onClick={() => setOpenMenu('')} aria-label="닫기">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <line x1="5" y1="5" x2="19" y2="19" />
                      <line x1="5" y1="19" x2="19" y2="5" />
                    </svg>
                  </button>
                </div>

                <div className="mnav_quick">
                  <button
                    type="button"
                    className="mnav_quick_btn"
                    onClick={() => window.open('https://ccu.learnchy.com/', '_blank')}
                  >
                    LMS
                  </button>

                  <button
                    type="button"
                    className="mnav_quick_btn"
                    onClick={() => {
                      navigate('/board/facility/write', { state: { step: 1 } });
                      handleMenuClose();
                    }}
                  >
                    시설예약
                  </button>
                </div>

                <div className="mnav_body">
                  <div className="mnav_left">
                    {mobileMenus.map((m) => (
                      <button
                        key={m.key}
                        type="button"
                        className={`mnav_tab ${mobileTab === m.key ? 'active' : ''}`}
                        onClick={() => setMobileTab(m.key)}
                      >
                        {m.left}
                      </button>
                    ))}
                  </div>

                  <div className="mnav_right">
                    <div className="mnav_title">{currentMobile.rightTitle}</div>

                    <div className="mnav_list">
                      {currentMobile.items.map((it, idx) => (
                        <button
                          key={`${currentMobile.key}-${idx}`}
                          type="button"
                          className="mnav_item"
                          onClick={it.onClick}
                        >
                          {it.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              {/* mnav_wrap */}
            </div>
          </div>
        </div>

        {/* ✅ PC web 전체폭 서브메뉴는 "중복(2개씩)" 원인이므로 JSX에서 제거 완료 */}
      </div>
    </div>
  );
};

export default Header;
